import { type ReactElement, useState, useRef, useEffect } from 'react'
import { ButtonSave, ButtonCancel, ButtonEdit, LockedFieldTooltip } from 'app/components'
import { Stack, TextInput, Group, Text } from '@mantine/core'
import { useToastError } from '@hooks'
import { useUpdateTeam, Size, useLockedTeamFields } from '@front/model'
import { ButtonVisibility } from '../ButtonVisibility'
import { useTeamHierarchyContext } from '../../TeamHierarchyContext'
import { type TeamNodeContentProps } from './types'

export const EditableNode = ({ team, isEditing, setIsEditing }: TeamNodeContentProps): ReactElement => {
  const { collapsedTeams, toggleTeamCollapse } = useTeamHierarchyContext()
  const [teamName, setTeamName] = useState(team.name)
  const inputRef = useRef<HTMLInputElement>(null)
  const { update, isUpdating, error } = useUpdateTeam()
  useToastError(error)
  const { lockedFields, isLoading: isLoadingLocks } = useLockedTeamFields(team.teamId)
  const isNameLocked = lockedFields.includes('name')

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  useEffect(() => {
    if (!isNameLocked) return
    setIsEditing(false)
    setTeamName(team.name)
  }, [isNameLocked, setIsEditing, team.name])

  const handleEdit = (): void => {
    setIsEditing(true)
  }

  const handleCancel = (): void => {
    setIsEditing(false)
    setTeamName(team.name)
  }

  const handleSave = async (): Promise<void> => {
    if (teamName.trim() === '') {
      setTeamName(team.name)
      setIsEditing(false)
      return
    }

    await update({
      teamId: team.teamId,
      name: teamName.trim(),
    })
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const handleToggleVisibility = (): void => {
    toggleTeamCollapse(team.teamId)
  }
  return (
    <Stack gap={Size.space.lg}>
      {isEditing ? (
        <TextInput
          disabled={isUpdating}
          miw={100}
          ref={inputRef}
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <Text className='text-center'>{team.name}</Text>
      )}
      <Group gap={Size.space.lg} justify='center' onClick={(e) => e.stopPropagation()}>
        {isEditing ? (
          <>
            <ButtonSave buttonType='icon-rounded' loading={isUpdating} onClick={handleSave} />
            <ButtonCancel buttonType='icon-rounded' onClick={handleCancel} />
          </>
        ) : (
          <LockedFieldTooltip locked={isNameLocked}>
            <ButtonEdit buttonType='icon-rounded' disabled={isLoadingLocks} variant='gray' onClick={handleEdit} />
          </LockedFieldTooltip>
        )}
        <ButtonVisibility isHiding={collapsedTeams.includes(team.teamId)} onClick={handleToggleVisibility} />
      </Group>
    </Stack>
  )
}
